import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { toast } from 'sonner'
import { debug } from '../lib/debug'
import { useNavigate } from 'react-router-dom'
import { SURVEY_CONFIG } from '../lib/constants'

interface Question {
  id: string
  question_text: string
  question_type: string
  aspect?: string
  spectrum?: string
  scale_min: number
  scale_max: number
  scale_label_min: string
  scale_label_max: string
  question_no: number
}

interface SelectedRole {
  roleId: string | null
  customRole: string | null
}

export function useSurveyQuestions(surveyCode: string) {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [questions, setQuestions] = useState<Question[]>([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [currentSection, setCurrentSection] = useState(0)
  const [responses, setResponses] = useState<Record<string, number>>(() => {
    const saved = localStorage.getItem(`survey_responses_${surveyCode}`)
    return saved ? JSON.parse(saved) : {}
  })
  const [selectedRole, setSelectedRole] = useState<SelectedRole | null>(() => {
    const saved = localStorage.getItem(`survey_role_${surveyCode}`)
    return saved ? JSON.parse(saved) : null
  })

  useEffect(() => {
    loadQuestions()
  }, [surveyCode])

  useEffect(() => {
    if (Object.keys(responses).length > 0) {
      localStorage.setItem(`survey_responses_${surveyCode}`, JSON.stringify(responses))
    }
  }, [responses, surveyCode])

  useEffect(() => {
    if (selectedRole) {
      localStorage.setItem(`survey_role_${surveyCode}`, JSON.stringify(selectedRole))
    }
  }, [selectedRole, surveyCode])

  const loadQuestions = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('survey_questions')
        .select('*')
        .eq('is_active', true)
        .order('question_no', { ascending: true })

      if (error) throw error

      if (!data || data.length === 0) {
        throw new Error('No questions found')
      }

      setQuestions(data)

      // Find the last answered question and set the appropriate section and index
      if (Object.keys(responses).length > 0) {
        let lastAnsweredIndex = -1
        let lastAnsweredSection = 0

        for (let i = 0; i < data.length; i++) {
          if (responses[data[i].id]) {
            lastAnsweredIndex = i
            if (data[i].question_type === 'aspect_agreement') {
              lastAnsweredSection = 1
            } else if (data[i].question_type === 'current_spectrum') {
              lastAnsweredSection = 2
            } else if (data[i].question_type === 'aspirational_spectrum') {
              lastAnsweredSection = 3
            } else if (data[i].question_type === 'effectiveness') {
              lastAnsweredSection = 4
            }
          } else {
            // Found first unanswered question, break here
            break
          }
        }

        // If we found answered questions, set the index to the next unanswered question
        if (lastAnsweredIndex >= 0) {
          const nextIndex = Math.min(lastAnsweredIndex + 1, data.length - 1)
          setCurrentQuestionIndex(nextIndex)
          setCurrentSection(lastAnsweredSection)
        }
      }
    } catch (error) {
      console.error('Error loading questions:', error)
      toast.error('Failed to load survey questions')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResponse = (value: number) => {
    const currentQuestion = questions[currentQuestionIndex]
    if (!currentQuestion) {
      console.error('No current question found')
      return
    }

    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }))
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1
      const prevQuestion = questions[prevIndex]
      const currentQuestion = questions[currentQuestionIndex]

      if (prevQuestion && currentQuestion && prevQuestion.question_type !== currentQuestion.question_type) {
        setCurrentSection(prev => prev - 1)
      }

      setCurrentQuestionIndex(prevIndex)
    } else if (currentSection > 0) {
      // If at the start of a section, find the last question of the previous section
      let prevSectionType = ''
      if (currentSection === 1) prevSectionType = 'role_selection'
      else if (currentSection === 2) prevSectionType = 'aspect_agreement'
      else if (currentSection === 3) prevSectionType = 'current_spectrum'
      else if (currentSection === 4) prevSectionType = 'aspirational_spectrum'

      if (prevSectionType === 'role_selection') {
        setCurrentSection(0)
      } else {
        const prevSectionQuestions = questions.filter(q => q.question_type === prevSectionType)
        if (prevSectionQuestions.length > 0) {
          const lastQuestionIndex = questions.findIndex(q => q.id === prevSectionQuestions[prevSectionQuestions.length - 1].id)
          setCurrentQuestionIndex(lastQuestionIndex)
          setCurrentSection(prev => prev - 1)
        } else {
          setCurrentSection(prev => prev - 1)
        }
      }
    }
  }

  const submitSurvey = async () => {
    try {
      const { error } = await supabase
        .from('survey_responses')
        .insert({
          survey_code: surveyCode,
          role: selectedRole?.roleId,
          custom_role_name: selectedRole?.customRole,
          responses,
          status: 'completed'
        })

      if (error) throw error

      // Clear local storage
      localStorage.removeItem(`survey_responses_${surveyCode}`)
      localStorage.removeItem(`survey_role_${surveyCode}`)

      // Show success message and redirect
      toast.success('Survey submitted successfully')
      navigate('/survey/thank-you')
    } catch (error) {
      console.error('Error submitting survey:', error)
      toast.error('Failed to submit survey. Please try again.')
    }
  }

  const handleNext = () => {
    if (currentSection === 0) {
      const firstQuestionIndex = questions.findIndex(q => q.question_type === 'aspect_agreement')
      if (firstQuestionIndex >= 0) {
        setCurrentSection(1)
        setCurrentQuestionIndex(firstQuestionIndex)
      }
      return
    }

    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1
      const nextQuestion = questions[nextIndex]
      const currentQuestion = questions[currentQuestionIndex]
      
      if (nextQuestion && currentQuestion && nextQuestion.question_type !== currentQuestion.question_type) {
        setCurrentSection(prev => prev + 1)
      }
      
      setCurrentQuestionIndex(nextIndex)
    } else {
      submitSurvey()
    }
  }

  const saveRole = (roleId: string | null, customRole: string | null) => {
    setSelectedRole({ roleId, customRole })
  }

  // Get current question safely
  const currentQuestion = questions[currentQuestionIndex]

  // Get questions for current section safely
  const questionsInSection = questions.filter(q => {
    if (!q) return false
    if (currentSection === 1) return q.question_type === 'aspect_agreement'
    if (currentSection === 2) return q.question_type === 'current_spectrum'
    if (currentSection === 3) return q.question_type === 'aspirational_spectrum'
    if (currentSection === 4) return q.question_type === 'effectiveness'
    return false
  })

  // Calculate if this is the last question in the current section
  const isLastInSection = currentQuestion && questionsInSection.length > 0
    ? questionsInSection[questionsInSection.length - 1].id === currentQuestion.id
    : false

  // Calculate if this is the last question overall
  const isLastQuestion = currentQuestionIndex === questions.length - 1

  // Calculate the current question number within its section
  const questionNumber = (() => {
    if (!currentQuestion || !questionsInSection.length) return 1;
    
    // Find the index of the current question within its section
    const index = questionsInSection.findIndex(q => q.id === currentQuestion.id);
    
    // Return 1-based index, defaulting to 1 if not found
    return index >= 0 ? index + 1 : 1;
  })();

  return {
    isLoading,
    questions,
    currentQuestion,
    currentQuestionIndex,
    responses,
    currentSection,
    selectedRole,
    saveRole,
    handleResponse,
    handlePrevious,
    handleNext,
    progress: currentSection === 0 ? 0 : (currentQuestionIndex + 1) / questions.length,
    questionsInSection,
    isLastInSection,
    isLastQuestion,
    questionNumber, // This will now always be at least 1
    hasError: questions.length === 0
  }
}